import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {UserSubscription} from "../../../../../../user/user-subscription";
import {User} from "../../../../../../user/user";
import {FormControl, Validators} from "@angular/forms";
import {DbValidators} from "../../../../../../shared/validators/DbValidators";
import {filter, first, switchMap} from "rxjs/operators";
import {BoosterService} from "../../../services/booster.service";
import {DbModalService} from "../../../../../../modules/modal/modal";
import {NotifierComponent} from "../../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../../shared/components/notifier/notification-type";
import {TextUtils} from "../../../../../../shared/utils/text-utils";
import {Subject} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../../store";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import {TypeUtils} from "../../../../../../shared/utils/type-utils";
import {Utils} from "../../../../../../shared/utils/utils";
import {ActivatedRoute, Router} from "@angular/router";
import {combineLatest} from "rxjs";
import {Website} from "../../../../website";
import {WebsiteService} from "../../../../../website.service";
import {IOPagesData} from "../../../mappers/io-list";
import {IBoosterStats} from "../../../../../../store/reducers/booster.reducer";
import {
  NotificationDialogComponent
} from "../../../../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {DialogService} from "../../../../../../../ng2-bootstrap-modal/dialog.service";
import {ImageOptimizerSuccessComponent} from "../../../image-optimizer-success/image-optimizer-success.component";

@Component({
    selector: 'db-image-optimizer-pages',
    templateUrl: './image-optimizer-pages.component.html',
    styleUrls: ['./image-optimizer-pages.component.scss'],
    host: { '[class.free-plan]': 'user.starterClient' },
    standalone: false
})
export class ImageOptimizerPagesComponent implements OnChanges, OnInit, OnDestroy {

  @Input() optimizedPages: IOPagesData[];
  @Input() optimizedPagesLoading: boolean;
  @Input() boosterPluginIsActive: boolean;
  @Input() stats: IBoosterStats;
  @Input() statsLoading: boolean;
  @Input() currentScore: any;


  public loading: boolean;
  public currentSubscription: UserSubscription;
  public user: User;
  public website: Website;
  public addedNewPage: boolean = false;

  public pageUrl: FormControl = new FormControl('');
  public serverError: string = null;
  public notConnected: boolean;
  public showPercentOffer: boolean;
  public demoUser: boolean = false;
  public reoptimizing: boolean = false;
  public choosedPage: number;
  public optimizedItemIndex: number = 0;
  public reoptimizationError = false;
  public noImagesError = false;
  @Output() getReport: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onClick: EventEmitter<void> = new EventEmitter<void>();
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _boosterService: BoosterService,
    private _dialogService: DialogService,
    private _websiteService: WebsiteService,
    private _dbModalService: DbModalService,
    private _cdRef: ChangeDetectorRef,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['optimizedPages'] && !changes['optimizedPages'].firstChange) {
      const currentValue = changes['optimizedPages'].currentValue;
      const previousValue = changes['optimizedPages'].previousValue;
      if (currentValue && previousValue && currentValue.length !== previousValue.length) {
        this.showIOSuccessModal();
      }
    }
  }

  ngOnInit() {
    this.website = this._route.snapshot.parent.parent.data['website'];
    if (!TypeUtils.isNull(this._route.snapshot.data['boosterTroubleshootData'])) {
      const disconnected = this._route.snapshot.data['boosterTroubleshootData'] === 'alive_but_not_connected';
      const restNoRoute = this._route.snapshot.data['boosterTroubleshootData'] === 'error';
      this.notConnected = disconnected || restNoRoute;
    }
    this._getUser();
    this._setValidators();
  }

  public validatePageUrl(goToUpgradePage: boolean): void {
    if (goToUpgradePage) {
      this.sendEvent('Dashboard Error', 'Pages limit exceeded (pop up)', ' IO: pages');
      this._dialogService.addDialog(NotificationDialogComponent, {
        header: 'You Exceeded the Limit of pages',
        message: 'Upgrade now and receive unlimited image optimization.',
        buttonText: 'Upgrade',
        action: 'upgrade',
        hasCloseButton: true,
        buttonColor: 'green'
      })
        .subscribe((confirmed: string) => {
          if (confirmed === 'upgrade') {
            this.sendEvent('Dashboard Action', 'Upgrade button click', 'Pages limit exceeded (pop up)');
            this._router.navigate(['/upgrade-plan']);
          }
        });
      return;
    }

    if (this.statsLoading || this.reoptimizing) {
      this.pageUrl.setValue('');
      this._showOptimizingInProgressDialog();
      return;
    }
    this.sendEvent('Dashboard Action', 'Optimize Button click', 'IO: pages tab');
    if (this.pageUrl.invalid) {
      return;
    }
    this.loading = true;
    this._optimizePage(this.pageUrl.value);
  }

  public onReOptimizeClick(pageUrl: string, index: number): void {
    if (this.statsLoading || this.reoptimizing) {
      this._showOptimizingInProgressDialog();
      return;
    }
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: 'You’re about to reoptimize your images',
      message: 'The selected mode will be applied to images that have already been optimized to further compress them.',
      buttonText: 'Reoptimize',
      action: 'reoptimize',
      hasCloseButton: true,
      buttonColor: 'green'
    }).subscribe((confirmed: string) => {
      if (confirmed === 'reoptimize') {
        this.sendEvent('Dashboard Action', 'Re-optimize Button click', 'IO: pages tab');
        this.optimizedItemIndex = index;
        this._reoptimize(pageUrl);
      }
    });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  public sendEvent(category: string, action: string, label: string): void {
    const analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      websiteId: this.website?.id
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private _showOptimizingInProgressDialog(): void {
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: 'Image optimization in progress',
      message: 'Please wait until the ongoing image optimization process<br/> is complete before beginning another' +
        ' optimization.',
      buttonText: 'Got It',
      hasCloseButton: true,
      buttonColor: 'green'
    });
  }

  private _getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        ))
      )
      .subscribe(([user, userSubscription]) => {
        this.user = user;
        this.currentSubscription = userSubscription;
        this.demoUser = Utils.isDemoUser(this.currentSubscription, user);
      });
  }

  private _optimizePage(pageUrl: string): void {
    this._cdRef.markForCheck();
    this.optimizedPagesLoading = true;
    this.addedNewPage = true;
    this.optimizedItemIndex = 0;
    this._websiteService.ioOptimize(this.website?.id, pageUrl)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (!response.success) {
          this.noImagesError = true;
          this.serverError = response && response.response && response.response.status === 404 ? 'No images to compress' : 'Optimization failed';
          this.sendEvent('Dashboard Error', 'Something Went Wrong', ' IO: pages tab');
        } else {
          setTimeout(() => {
            this.getReport.emit(true);
            NotifierComponent.notify(new Notification(TextUtils.getText('text_page_added'), NotificationType.SUCCESS));
            /*TODO SONA check*/
          }, 3000);
        }
        this.pageUrl.setValue(' ');
        this.optimizedPagesLoading = false;
        this.loading = false;
      });
  }

  private _reoptimize(url: string): void {
    this.reoptimizing = true;
    this._websiteService.ioOptimize(this.website?.id, url, 1)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (!response.success) {
          this.reoptimizationError = true;
          this.sendEvent('Dashboard Error', 'Something Went Wrong', ' IO: pages tab');
        } else {
          setTimeout(() => {
            NotifierComponent.notify(new Notification(TextUtils.getText('text_page_reoptimized'), NotificationType.SUCCESS));
            this.optimizedPagesLoading = false;
          }, 2000);
        }
        this.reoptimizing = false;
      });
  }

  private _setValidators(): void {
    const regexp = new RegExp(Utils.replaceUrl(this.website.site_url) + '+');
    const validators = [Validators.required, Validators.pattern(regexp), DbValidators.trim];
    this.pageUrl.setValidators(validators);
  }

  private showIOSuccessModal(): void {
    const a = this._dbModalService.hasOpenModals();
    const b = this._dialogService.hasOpenedDialog();
    if (this._dbModalService.hasOpenModals() || this._dialogService.hasOpenedDialog()) {
      return;
    }
    if (this.boosterPluginIsActive) {
      this._dialogService.addDialog(NotificationDialogComponent, {
        header: 'Images are successfully optimized',
        message: 'Check your website and see the results.',
        buttonText: 'Got It',
        hasCloseButton: true,
        buttonColor: 'green'
      });
      return;
    }
    setTimeout(() => {
      const modal = this._dbModalService.open(ImageOptimizerSuccessComponent, {height: 600, width: 940});
      modal.componentInstance.report = this.optimizedPages[this.optimizedItemIndex];
      modal.componentInstance.website = this.website;
      modal.componentInstance.currentScore = this.currentScore;
    }, 3000);
  }

}
